import { Head, router } from '@inertiajs/react';
import { useState } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import Badge from '@/Components/Badge';
import PageHeader from '@/Components/PageHeader';
import Modal, { ModalBody, ModalFooter } from '@/Components/Modal';
import Alert from '@/Components/Alert';
import {
    ArrowLeftIcon, ArrowDownTrayIcon, DocumentArrowDownIcon, TrashIcon,
    DevicePhoneMobileIcon, ComputerDesktopIcon, DeviceTabletIcon, ChartBarIcon
} from '@heroicons/react/24/outline';

function DetailRow({ label, children }) {
    return (
        <div className="flex justify-between items-center py-3 border-b border-slate-100 last:border-0">
            <span className="text-sm text-slate-500">{label}</span>
            <div className="text-sm font-medium text-slate-800">{children}</div>
        </div>
    );
}

function DeviceIcon({ type }) {
    if (type === 'mobile') return <DevicePhoneMobileIcon className="w-4 h-4" />;
    if (type === 'tablet') return <DeviceTabletIcon className="w-4 h-4" />;
    return <ComputerDesktopIcon className="w-4 h-4" />;
}

export default function Show({ qrCode, scansByDay, scansByDevice, flash }) {
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const isValidQr = qrCode.qr_code_path && qrCode.qr_code_path !== 'temp';

    return (
        <AppLayout>
            <Head title={`QR Code: ${qrCode.title}`} />
            <PageHeader
                title={qrCode.title}
                subtitle="QR Code Details"
                actions={
                    <div className="flex gap-2">
                        <Button href="/qr-codes" variant="outline" size="sm">
                            <ArrowLeftIcon className="w-4 h-4" /> Back
                        </Button>
                        {isValidQr && (
                            <>
                                <a
                                    href={`/qr-codes/${qrCode.id}/download`}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg transition-colors"
                                >
                                    <ArrowDownTrayIcon className="w-4 h-4" /> SVG
                                </a>
                                <a
                                    href={`/qr-codes/${qrCode.id}/download-pdf`}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-slate-700 hover:bg-slate-800 text-white rounded-lg transition-colors"
                                >
                                    <DocumentArrowDownIcon className="w-4 h-4" /> PDF
                                </a>
                            </>
                        )}
                        <Button variant="danger" size="sm" onClick={() => setDeleteConfirm(true)}>
                            <TrashIcon className="w-4 h-4" /> Delete
                        </Button>
                    </div>
                }
            />

            {flash?.success && <Alert type="success" className="mb-4">{flash.success}</Alert>}
            {flash?.error && <Alert type="danger" className="mb-4">{flash.error}</Alert>}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* QR Code Image */}
                <Card>
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">QR Code</h3>
                    </div>
                    <CardBody className="flex flex-col items-center justify-center p-8">
                        {isValidQr ? (
                            <img
                                src={`/storage/${qrCode.qr_code_path}`}
                                alt={qrCode.title}
                                className="w-64 h-64 rounded-xl border border-slate-200 p-2 bg-white"
                            />
                        ) : (
                            <div className="w-64 h-64 bg-slate-100 rounded-xl flex items-center justify-center">
                                <div className="text-center">
                                    <ChartBarIcon className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                                    <p className="text-sm text-slate-400">QR code not generated</p>
                                    <p className="text-xs text-slate-300 mt-1">The image may have failed to generate</p>
                                </div>
                            </div>
                        )}
                        {qrCode.public_url && (
                            <div className="mt-4 w-full max-w-sm">
                                <p className="text-xs text-slate-500 mb-1 text-center">Scan URL</p>
                                <div className="bg-slate-50 rounded-lg px-3 py-2 text-xs font-mono text-slate-600 text-center break-all">
                                    {qrCode.public_url}
                                </div>
                            </div>
                        )}
                    </CardBody>
                </Card>

                {/* Details */}
                <Card>
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">Details</h3>
                    </div>
                    <CardBody>
                        <DetailRow label="Title">{qrCode.title}</DetailRow>
                        <DetailRow label="Slug">
                            <span className="font-mono text-slate-600">{qrCode.slug}</span>
                        </DetailRow>
                        <DetailRow label="Type">
                            <Badge variant={qrCode.type === 'url' ? 'info' : 'purple'}>
                                {qrCode.type === 'url' ? 'URL' : 'File'}
                            </Badge>
                        </DetailRow>
                        {qrCode.type === 'url' && qrCode.url && (
                            <DetailRow label="Redirect URL">
                                <a href={qrCode.url} target="_blank" rel="noopener noreferrer" className="text-emerald-800 hover:underline max-w-[200px] truncate block">
                                    {qrCode.url}
                                </a>
                            </DetailRow>
                        )}
                        <DetailRow label="Scans">
                            <Badge variant={qrCode.scan_count > 0 ? 'info' : 'neutral'}>
                                {qrCode.scan_count || 0} scans
                            </Badge>
                        </DetailRow>
                        <DetailRow label="Status">
                            <Badge variant={qrCode.is_active ? 'success' : 'neutral'}>
                                {qrCode.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                        </DetailRow>
                        <DetailRow label="Created">
                            {new Date(qrCode.created_at).toLocaleDateString()}
                        </DetailRow>
                        {qrCode.description && (
                            <div className="mt-4 p-4 bg-slate-50 rounded-xl">
                                <p className="text-xs font-medium text-slate-500 mb-1">Description</p>
                                <p className="text-sm text-slate-700">{qrCode.description}</p>
                            </div>
                        )}
                    </CardBody>
                </Card>

                {/* Uploaded Files (for file type) */}
                {qrCode.type === 'file' && (
                    <Card>
                        <div className="px-6 py-4 border-b border-slate-100">
                            <h3 className="font-semibold text-slate-800">Uploaded Files</h3>
                        </div>
                        <CardBody>
                            <div className="space-y-2">
                                {qrCode.image_url && (
                                    <a href={qrCode.image_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors">
                                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                            <span className="text-xs font-bold text-blue-600">IMG</span>
                                        </div>
                                        <span className="text-sm text-slate-700">Product Image</span>
                                    </a>
                                )}
                                {qrCode.certificate_url && (
                                    <a href={qrCode.certificate_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors">
                                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                                            <span className="text-xs font-bold text-green-600">CERT</span>
                                        </div>
                                        <span className="text-sm text-slate-700">Certificate</span>
                                    </a>
                                )}
                                {qrCode.model3d_url && (
                                    <a href={qrCode.model3d_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors">
                                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                                            <span className="text-xs font-bold text-purple-600">3D</span>
                                        </div>
                                        <span className="text-sm text-slate-700">3D Model (.glb)</span>
                                    </a>
                                )}
                                {qrCode.cv_url && (
                                    <a href={qrCode.cv_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors">
                                        <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                                            <span className="text-xs font-bold text-amber-600">CV</span>
                                        </div>
                                        <span className="text-sm text-slate-700">CV / Document</span>
                                    </a>
                                )}
                                {!qrCode.image_url && !qrCode.certificate_url && !qrCode.model3d_url && !qrCode.cv_url && (
                                    <p className="text-sm text-slate-400 py-3">No files uploaded.</p>
                                )}
                            </div>
                        </CardBody>
                    </Card>
                )}

                {/* Scan Analytics - Device Breakdown */}
                {scansByDevice && scansByDevice.length > 0 && (
                    <Card>
                        <div className="px-6 py-4 border-b border-slate-100">
                            <h3 className="font-semibold text-slate-800">Scans by Device</h3>
                        </div>
                        <CardBody>
                            <div className="space-y-3">
                                {scansByDevice.map((device, i) => {
                                    const total = scansByDevice.reduce((sum, d) => sum + d.count, 0);
                                    const percentage = total > 0 ? ((device.count / total) * 100).toFixed(1) : 0;
                                    return (
                                        <div key={i} className="space-y-1">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2 text-sm text-slate-700">
                                                    <DeviceIcon type={device.device_type} />
                                                    <span className="capitalize">{device.device_type || 'Unknown'}</span>
                                                </div>
                                                <span className="text-sm font-medium text-slate-800">{device.count} ({percentage}%)</span>
                                            </div>
                                            <div className="w-full bg-slate-100 rounded-full h-2">
                                                <div
                                                    className={`h-2 rounded-full ${
                                                        device.device_type === 'mobile' ? 'bg-blue-500' :
                                                        device.device_type === 'tablet' ? 'bg-purple-500' : 'bg-emerald-700'
                                                    }`}
                                                    style={{ width: `${percentage}%` }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardBody>
                    </Card>
                )}

                {/* Scan Analytics - Recent Activity */}
                {scansByDay && scansByDay.length > 0 && (
                    <Card className={qrCode.type !== 'file' ? 'lg:col-span-2' : ''}>
                        <div className="px-6 py-4 border-b border-slate-100">
                            <h3 className="font-semibold text-slate-800">Recent Scan Activity (Last 30 Days)</h3>
                        </div>
                        <CardBody>
                            <div className="space-y-2">
                                {scansByDay.map((day, i) => {
                                    const maxCount = Math.max(...scansByDay.map(d => d.count));
                                    const width = maxCount > 0 ? ((day.count / maxCount) * 100) : 0;
                                    return (
                                        <div key={i} className="flex items-center gap-3">
                                            <span className="text-xs text-slate-500 w-24 shrink-0">
                                                {new Date(day.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                            </span>
                                            <div className="flex-1 bg-slate-100 rounded-full h-5 relative">
                                                <div
                                                    className="bg-emerald-700 h-5 rounded-full transition-all flex items-center justify-end pr-2"
                                                    style={{ width: `${Math.max(width, 8)}%` }}
                                                >
                                                    <span className="text-[10px] font-medium text-white">{day.count}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardBody>
                    </Card>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            <Modal
                show={deleteConfirm}
                onClose={() => setDeleteConfirm(false)}
                title="Delete QR Code"
                iconBg="bg-red-100"
                iconColor="text-red-600"
                icon={<TrashIcon className="w-5 h-5" />}
            >
                <ModalBody>
                    <p className="text-sm text-slate-600">
                        Are you sure you want to delete <strong>{qrCode.title}</strong>?
                        This will remove the QR code image, associated files, and all scan history.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button variant="outline" onClick={() => setDeleteConfirm(false)}>Cancel</Button>
                    <Button
                        variant="danger"
                        onClick={() => {
                            router.delete(`/qr-codes/${qrCode.id}`);
                        }}
                    >
                        Delete
                    </Button>
                </ModalFooter>
            </Modal>
        </AppLayout>
    );
}
